<template>
  <div>
    <div class="d-flex flex-wrap align-items-center justify-content-between">
      <h1 id="headline" class="page-header mr-3">All Screens</h1>
      <div class="d-flex align-items-center my-2">
        <div>
          <b-input-group>
            <b-input-group-prepend>
              <b-input-group-text>
                <font-awesome-icon icon="search"/>
              </b-input-group-text>
            </b-input-group-prepend>
            <b-form-input v-model="filter" debounce="500" placeholder="Filter" type="text"></b-form-input>
          </b-input-group>
        </div>
        <b-button v-if="isUserAdmin || isUserEngineeringManager" v-b-modal="'addMachine'" variant="outline-dark" class="ml-3">
          <font-awesome-icon icon="plus"/>
          <span class="ml-2 d-none d-sm-inline">Add Screen</span>
        </b-button>
      </div>
    </div>
    <b-table
        id="machines-table"
        ref="machines-table"
        :busy.sync="isBusy"
        :current-page="currentPage"
        :fields="fields"
        :filter="filter"
        :items="machinesProvider"
        :per-page="perPage"
        :sort-by.sync="sortBy"
        :sort-desc.sync="sortDesc"
        responsive="sm"
        show-empty
        small
        striped
    >
      <template #cell(serial)="data">
        <router-link :to="'/machine/' + encodeURIComponent(data.item.serial)">
          <font-awesome-icon icon="barcode" class="mr-1"/>
          <span style="white-space: pre;">{{ data.item.serial }}</span>
        </router-link>
        <b-badge v-if="(isUserAdmin || isUserEngineeringManager) && hasInvalidSerial(data.item.serial)" variant="warning" class="ml-2" title="Contains trailing spaces or invalid characters">
          <font-awesome-icon icon="exclamation-circle" class="mr-1"/>
          <span>Invalid Serial</span>
        </b-badge>
      </template>
      <template #cell(reviewCount)="data">
        <div v-if="data.item.reviewCount > 0">
          <open-reviews-notification :machineSerial="data.item.serial" identifier="machinesNotifications"/>
        </div>
      </template>
      <template #empty="">
        <div aria-live="polite" role="alert">
          <div class="text-center my-2">
            <b-spinner v-if="isBusy" :style="'visibility: ' + (isBusy ? 'visible': 'hidden')" label="Loading..."
                       variant="secondary"></b-spinner>
            <span v-else>There are no records matching your request</span>
          </div>
        </div>
      </template>
      <template #emptyfiltered="">
        <div aria-live="polite" role="alert">
          <div class="text-center my-2">
            <span>There are no records matching your request</span>
          </div>
        </div>
      </template>
    </b-table>
    <div v-if="statusText" class="mb-3">
      {{ statusText }}
    </div>
    <div class="d-flex justify-content-between align-items-center mb-3">
      <span class="text-muted">
        {{ totalRows }} results
        <b-spinner :style="'visibility: ' + (isBusy ? 'visible': 'hidden')" label="Loading..." small
                   variant="secondary"></b-spinner>
      </span>
      <b-pagination
          v-model="currentPage"
          :per-page="perPage"
          :total-rows="totalRows"
          aria-controls="machines-table"
          class="mb-0"
          first-number
          last-number
          limit="7"
          size="sm"
      ></b-pagination>
      <b-form inline>
        <span class="text-muted">Show</span>
        <b-form-select v-model="perPage" :options="perPageOptions" class="ml-2" size="sm"></b-form-select>
      </b-form>
    </div>

    <b-modal id="addMachine" ref="addMachine" title="Add" @cancel="resetMachineModal" @ok.prevent="addMachine" :busy="isBusy">
      <b-alert v-if="addMachineError" show variant="danger">{{ addMachineError }}</b-alert>
      <div class="form-group row">
        <label class="col-sm-2 col-form-label" for="serial">S/N</label>
        <div class="col-sm-10">
          <div class="input-group">
            <div class="input-group-prepend"><span class="input-group-text"><font-awesome-icon icon="barcode"/></span>
            </div>
            <input id="serial" v-model="modalSerial" class="form-control" name="serial" style="text-transform:uppercase"
                   type="text" @input="serialInput" @keypress="serialKeyPress">
          </div>
          <small class="form-text text-muted">
            Allowed: letters, numbers, spaces, dots, and hyphens. Leading/trailing spaces will be trimmed automatically.
          </small>
          <small v-if="serialInputWarning" class="form-text text-danger">
            Invalid characters removed. Allowed: A-Z, 0-9, space, dot, hyphen.
          </small>
        </div>
      </div>
      <div class="form-group row">
        <label class="col-sm-2 col-form-label" for="type">Type</label>
        <div class="col-sm-10">
          <div class="input-group">
            <div class="input-group-prepend"><span class="input-group-text"><font-awesome-icon icon="tags"/></span>
            </div>
            <input id="type" v-model="modalType" class="form-control" name="type" type="text">
          </div>
        </div>
      </div>
      <div class="form-group row">
        <label class="col-sm-2 col-form-label" for="owner">Owner</label>
        <div class="col-sm-10">
          <div class="input-group">
            <div class="input-group-prepend"><span class="input-group-text"><font-awesome-icon icon="briefcase"/></span>
            </div>
            <input id="owner" v-model="modalOwner" class="form-control" name="owner" type="text">
          </div>
        </div>
      </div>
      <template #modal-ok>
        <b-spinner v-if="isBusy" small></b-spinner>
        <span v-else>OK</span>
      </template>
    </b-modal>
  </div>
</template>

<script>

import { machinesApi } from '@/backend'
import { EditMachineQuery } from '@/screencheck_portal_api/src'
import OpenReviewsNotification from '@/components/machine_file/OpenReviewsNotification.vue'
import { mapGetters } from 'vuex'

export default {
  name: 'MachinesPage',
  components: { OpenReviewsNotification },
  data: function () {
    return {
      statusText: null,
      totalRows: 0,
      isBusy: false,
      fields: [
        { key: 'serial', label: 'Screen Serial', sortable: true },
        { key: 'type', label: 'Screen Type', sortable: true },
        { key: 'owner', label: 'Screen Owner', sortable: true },
        { key: 'reportCount', label: 'Number of Reports', sortable: true },
        { key: 'fileCount', label: 'Number of Files', sortable: true },
        { key: 'reviewCount', label: 'Open Reviews', sortable: true }
      ],
      filter: '',
      sortBy: null,
      sortDesc: false,
      perPage: 25,
      currentPage: 1,
      perPageOptions: [10, 25, 50, 100],
      modalSerial: '',
      modalType: '',
      modalOwner: '',
      addMachineError: null,
      serialInputWarning: false
    }
  },
  computed: {
    ...mapGetters(['isUserAdmin', 'isUserEngineeringManager']),
  },
  methods: {
    hasInvalidSerial: function (serial) {
      if (!serial) return false
      return serial !== serial.trim() || /[^A-Z0-9 .-]/.test(serial)
    },
    addMachine: function () {
      const cleanSerial = this.modalSerial.trim().toUpperCase()
      let editMachineQuery = EditMachineQuery.constructFromObject({
        serial: cleanSerial,
        type: this.modalType,
        owner: this.modalOwner,
        data: null
      })
      this.isBusy = true
      machinesApi.addMachine(editMachineQuery, (error) => {
        this.isBusy = false
        if (error) {
          console.error(error)
          this.statusText = 'Error creating machine'
          this.addMachineError = 'Error creating machine. ' + error.response.body.message
          return
        }
        this.$router.push({ name: 'machine', params: { serial: cleanSerial } })
        this.resetMachineModal()
        this.$refs['addMachine'].hide()
      })
    },
    resetMachineModal: function () {
      this.modalSerial = ''
      this.modalType = ''
      this.modalOwner = ''
      this.addMachineError = null
      this.serialInputWarning = false
    },
    serialInput: function () {
      if (this.modalSerial.match(/[^a-zA-Z0-9 .-]/g)) {
        this.serialInputWarning = true
        this.modalSerial = this.modalSerial.toUpperCase().replace(/[^A-Z0-9 .-]/g, '')
      } else {
        this.serialInputWarning = false
      }
    },
    serialKeyPress: function (ev) {
      if (ev.ctrlKey || ev.metaKey || ev.altKey) {
        return
      }
      const char = ev.key && ev.key.length === 1 ? ev.key : (ev.which ? String.fromCharCode(ev.which) : null)
      if (char && char.length === 1 && ev.keyCode !== 13 && ev.keyCode !== 9 && ev.keyCode !== 8 && ev.keyCode !== 46) {
        if (/[^a-zA-Z0-9 .-]/.test(char)) {
          ev.preventDefault()
          this.serialInputWarning = true
          return
        }
      }
      this.serialInputWarning = false
    },
    machinesProvider: function (ctx, callback) {
      let query = {
        filter: ctx.filter,
        sortBy: ctx.sortBy,
        sortDesc: ctx.sortDesc,
        currentPage: ctx.currentPage,
        perPage: ctx.perPage
      }
      this.isBusy = true
      machinesApi.getAllMachines(query, (error, data) => {
        this.isBusy = false
        if (error) {
          console.error(error)
          this.statusText = 'Error transferring machines'
          callback([])
          return
        }
        this.totalRows = data.totalRows
        callback(data.machines)
      })
      // Required to use callback
      return null
    }
  }
}
</script>

<style lang="scss">
</style>
